<script setup lang="ts">
import { Primitive, type PrimitiveProps } from "radix-vue";
import { buttonVariants } from ".";
import { cn } from "@/lib/utils";
import LoadingDots from "../loading-dots/LoadingDots.vue";
import { Check } from "lucide-vue-next";
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

interface Props extends PrimitiveProps {
    variant?: NonNullable<Parameters<typeof buttonVariants>[0]>["variant"];
    size?: NonNullable<Parameters<typeof buttonVariants>[0]>["size"];
    as?: string;
    recentlySuccessful?: boolean;
    loading?: boolean;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    as: "button",
    recentlySuccessful: false,
    loading: false,
    disabled: false,
});

const buttonRef = useTemplateRef<HTMLElement>("buttonRef");
const buttonWidth = ref("auto");

// loading makes button disabled immediately but don't want to jump between loading
// and recently saved too quickly, thats why we delay the loading dots by 150 ms
const loadingDelayed = ref(false);
let timeoutId: ReturnType<typeof setTimeout> | null = null;

watch(
    () => props.loading,
    (newVal) => {
        if (newVal) {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                loadingDelayed.value = true;
            }, 300);
        } else {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            loadingDelayed.value = false;
        }
    },
);

function getButtonWidthSoItsFixedForLoadingAndRecentlySaved() {
    // Keep the width for icon
    if (props.size === "icon") {
        buttonWidth.value = `40px`;
        return;
    }

    if (buttonRef.value) {
        if (props.variant !== "ghost") {
            // @ts-expect-error
            buttonWidth.value = `${Math.ceil(buttonRef.value.$el.offsetWidth)}px`;
        } else {
            // @ts-expect-error
            buttonWidth.value = `${Math.ceil(buttonRef.value.$el.offsetWidth + 1)}px`;
        }
    }
}

onMounted(async () => {
    await nextTick();

    getButtonWidthSoItsFixedForLoadingAndRecentlySaved();
});
</script>

<template>
    <Primitive
        :as="as"
        ref="buttonRef"
        :as-child="asChild"
        :disabled="disabled || loading"
        :class="cn(buttonVariants({ variant, size }), $attrs.class ?? '')"
    >
        <LoadingDots v-if="loadingDelayed && !recentlySuccessful"></LoadingDots>

        <Transition name="bounce">
            <Check v-if="recentlySuccessful"></Check>
        </Transition>

        <template v-if="!loadingDelayed && !recentlySuccessful">
            <slot />
        </template>
    </Primitive>
</template>

<style>
.bounce-enter-active {
    animation: bounce-in 0.5s;
}

@keyframes bounce-in {
    0% {
        transform: scale(0);
    }
    50% {
        transform: scale(1.25);
    }
    100% {
        transform: scale(1);
    }
}
</style>
